import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface RuleTemplate {
  group: string;
  rule: string;
  read: boolean;
  write: boolean;
  edit: boolean;
  description: string;
  isDependent?: boolean; // Optionnel pour RoleComponent
}

// Default roles of the gateway (DefaultRoles, DMP2-846): none can be deleted, and ROLE_ADMIN cannot be changed.
// The gateway refuses these operations anyway: this list only hides the actions.
export const LOCKED_ROLE = 'ROLE_ADMIN';
export const DEFAULT_ROLES = [LOCKED_ROLE, 'ROLE_LECTURE', 'ROLE_MARKETING', 'ROLE_FACTURATION'];

@Injectable({
  providedIn: 'root'
})
export class RoleRulesService {

  // Presentation of the rules (group, order, description). Which rules exist comes from GET /api/rule/.
  static readonly RULES_TEMPLATE: RuleTemplate[] = [
    {group: '1. Paramétrage', rule: 'DATASTORE', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '1. Paramétrage', rule: 'TABLES', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '1. Paramétrage', rule: 'DATAVIEWSETTINGS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '1. Paramétrage', rule: 'TAGS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '1. Paramétrage', rule: 'PROVIDERS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '1. Paramétrage', rule: 'BREVO_CONFIG', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '2. Flux', rule: 'WORKFLOW', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '2. Flux', rule: 'IMPORTS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '2. Flux', rule: 'EXPORTS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '2. Flux', rule: 'TRANSFERS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '2. Flux', rule: 'DICO', read: false, write: false, edit: false, description: '', isDependent: true},
    {group: '2. Flux', rule: 'TASKS_IMPORT', read: false, write: false, edit: false, description: 'Tâches d\'import', isDependent: true},
    {group: '2. Flux', rule: 'TASKS_EXPORT', read: false, write: false, edit: false, description: 'Tâches d\'export', isDependent: true},
    {group: '3. Gestion', rule: 'DATAVIEWS', read: false, write: false, edit: false, description: 'Accès aux pages Commandes, Contacts et Leads', isDependent: false},
    {group: '3. Gestion', rule: 'TAGEVENTS', read: false, write: false, edit: false, description: '', isDependent: true},
    {group: '3. Gestion', rule: 'SUPERSET_DASHBOARD', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '4. Marketing', rule: 'CAMPAIGN', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '4. Marketing', rule: 'SUBSCRIPTIONS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '4. Marketing', rule: 'HTMLBLOCK', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '4. Marketing', rule: 'EMAILTRANSACTIONAL', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '5. Landing page', rule: 'HTML5TEMPLATE', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '5. Landing page', rule: 'LANDINGPAGECONFIGURATION', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '6. Appel à facturation', rule: 'INVOICECONTACTS', read: false, write: false, edit: false, description: '', isDependent: false},
    {group: '6. Appel à facturation', rule: 'INVOICECOMPANIES', read: false, write: false, edit: false, description: '', isDependent: false}
  ];

  // Rows that follow another feature and are never saved, with the feature they follow.
  static readonly FOLLOWED_RULES: Record<string, string> = {
    DICO: 'IMPORTS',
    TASKS_IMPORT: 'IMPORTS',
    TASKS_EXPORT: 'EXPORTS',
    TAGEVENTS: 'DATAVIEWS'
  };

  constructor(private readonly translate: TranslateService) {
  }

  getRulesTemplate(): RuleTemplate[] {
    return JSON.parse(JSON.stringify(RoleRulesService.RULES_TEMPLATE)); // Deep copy
  }

  /**
   * Rows for the features the API knows ("<LEVEL>_<RULE>" rules from GET /api/rule/): the template rows whose
   * feature exists, then the features the template does not know, in an "other" group.
   */
  getRules(apiRules: string[]): RuleTemplate[] {
    const features = new Set(apiRules.map(rule => RoleRulesService.splitRule(rule).feature));
    const rows = this.getRulesTemplate()
      .filter(row => features.has(RoleRulesService.FOLLOWED_RULES[row.rule] ?? row.rule));
    const known = new Set(rows.map(row => row.rule));
    const otherGroup = `9. ${this.translate.instant('roleEdit.permissions.otherGroup')}`;
    features.forEach(feature => {
      if (!known.has(feature)) {
        rows.push({group: otherGroup, rule: feature, read: false, write: false, edit: false, description: '', isDependent: false});
      }
    });
    return rows;
  }

  // "READ_BREVO_CONFIG" -> level "READ", feature "BREVO_CONFIG": only the first "_" separates them.
  static splitRule(rule: string): { level: string, feature: string } {
    const index = rule.indexOf('_');
    return index < 0 ? {level: rule, feature: ''} : {level: rule.substring(0, index), feature: rule.substring(index + 1)};
  }
}
